import clsx from 'clsx';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import ChevronRightIcon from '@/assets/icons/chevron-right.svg';
import ErrorIcon from '@/assets/icons/exclamation-circle.svg';
import { Button } from '@/components/ui/button';
import { ChannelIcon } from '@/components/ui/channel-icon';
import { Tooltip } from '@/components/ui/tooltip';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import { POOL } from '@/types/channels';
import { getChannelName } from '@/utils/channel-names';
import { formatShortDate } from '@/utils/date-util';

interface ChannelSummaryProps {
  channelSlug: string;
  setIsExpanded?: (isExpanded: boolean) => void;
}

export function ChannelSummary({
  channelSlug,
  setIsExpanded,
}: ChannelSummaryProps) {
  const intl = useIntl();
  const { formatCurrency } = useCurrencySymbol();
  const { hookForm } = useJobPostingContext();
  const {
    watch,
    setValue,
    formState: { errors },
  } = hookForm;

  const hasErrors = errors?.channels?.[channelSlug];

  const isPool = channelSlug === POOL;
  const startsAt = isPool
    ? watch('pool.startsAt')
    : watch(`channels.${channelSlug}.startsAt`);
  const endAt = isPool
    ? watch('pool.endAt')
    : watch(`channels.${channelSlug}.endAt`);
  const totalChannelBudget = watch(
    `channels.${channelSlug}.totalChannelBudget`,
  );
  const isExpanded = watch(`channels.${channelSlug}.isExpanded`);

  const toggleExpanded = () => {
    if (setIsExpanded) {
      setIsExpanded(!isExpanded);
      return;
    }

    setValue(`channels.${channelSlug}.isExpanded`, !isExpanded, {
      shouldDirty: false,
    });
  };

  const displayBudget = useMemo(() => {
    if (isPool)
      return intl.formatMessage({
        defaultMessage: 'Free',
        description: 'Free budget',
      });

    if (!totalChannelBudget) return '-';

    return formatCurrency(totalChannelBudget);
  }, [formatCurrency, intl, isPool, totalChannelBudget]);

  return (
    <div
      className={clsx(
        'border-fx-neutral-200 mb-2 w-[640px] rounded-lg border bg-white p-6 shadow-sm',
        hasErrors && 'border-fx-rose',
      )}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-6">
          <ChannelIcon
            channelSlug={channelSlug}
            variant="large"
            channelName={getChannelName(channelSlug)}
          />
          <Typography variant="body1">
            {formatShortDate(startsAt) ?? 'Today'} -
            {endAt ? formatShortDate(endAt) : 'Ongoing'}
          </Typography>
        </div>

        <div className="flex items-center gap-4">
          {hasErrors && (
            <Tooltip
              content={intl.formatMessage({
                defaultMessage:
                  'Some fields are incomplete or have invalid entries. Please review and fix the highlighted fields.',
                description: 'Error message for required fields',
              })}
            >
              <img src={ErrorIcon} alt="Error" />
            </Tooltip>
          )}
          <Typography variant="h4" className="!text-fx-neutral-600">
            {displayBudget}
          </Typography>
          <Button
            type="button"
            variant="icon"
            className="hover:!bg-fx-neutral-100"
            onClick={toggleExpanded}
          >
            <img src={ChevronRightIcon} alt="Chevron right" />
          </Button>
        </div>
      </div>
    </div>
  );
}
